import { ProviderFilterTypes } from 'dataSource/ProviderSearch'
import { providerFilterKeyMapping } from 'dataSource/ProviderSearch'
import _isEqual from 'lodash/isEqual'
import _reduce from 'lodash/reduce'
import _size from 'lodash/size'
import _omit from 'lodash/omit'
import { RudderstackIntegrations, sessionStorageKeys } from 'constants/common'
import { RudderEvent } from 'constants/rudderEvents'
import logEvent from 'utils/logEvent'
import { ParsedUrlQuery } from 'querystring'
import { pageRoutes } from 'constants/routing'

// providers -> this would be the total providers, direct + indirect matches
// countDetails -> this would be the count of slots, direct + indirect
// totalProvidersAvailable -> this would be the total providers count, direct + indirect

type AnalyticsProps = {
  providers: Provider[] | ProviderCardDetails[]
  countDetails: { slot_sum: number; provider_count: ProviderCount[] }
  totalProvidersAvailable: number | undefined
  groupedFilters: GroupedProviderFilter
  query: ParsedUrlQuery
  startSlotDate: Date
  selectedPractice: PracticeProvider
  user: Partial<User>
  intakeSupplyReserveParams: Record<string, unknown>
  directProvidersCount?: number
  indirectProvidersCount?: number
  directSlotsCountDetails?: {
    slot_sum: number
    provider_count: ProviderCount[]
  }
  indirectSlotsCountDetails?: {
    slot_sum: number
    provider_count: ProviderCount[]
  }
  timeZone?: string
  /** True when the red "Show more providers" control caused this shown event. */
  showMoreProviders?: boolean
  /** True when the blue "Find more partial matches" control caused this shown event. */
  findMorePartialMatches?: boolean
  /**
   * Display labels of filters the waterfall has removed. Present on every
   * shown event. Empty until a filter is actually dropped, and unchanged by
   * "Show more providers".
   */
  relaxedFilters?: (string | number)[]
}

export const callRudderEventForAvailableProviderShown = ({
  providers,
  countDetails,
  totalProvidersAvailable,
  groupedFilters,
  query,
  startSlotDate,
  selectedPractice,
  user,
  intakeSupplyReserveParams,

  directProvidersCount,
  indirectProvidersCount,
  directSlotsCountDetails,
  indirectSlotsCountDetails,
  timeZone = '',
  showMoreProviders = false,
  findMorePartialMatches = false,
  relaxedFilters = [],
}: AnalyticsProps): void => {
  const selectedTherapyType = groupedFilters['counseling_type']?.find(
    (counselingType) => counselingType.id === query['counselingType[]']
  )?.value
  const selectedAppointmentType = groupedFilters[
    ProviderFilterTypes.AVAILABILITY_TYPE
  ]?.find(
    (appointmentMode) => appointmentMode.id === query['availabilityType[]']
  )?.value

  const getSeletedFilterValues = (filterKey: string) => {
    const filterType = providerFilterKeyMapping[filterKey]
    const queryValue = query[filterKey]
    const queryValueArray = Array.isArray(queryValue)
      ? queryValue
      : [queryValue]

    const selectedFilterValue = groupedFilters[filterType]
      ?.filter((filter) => queryValueArray.includes(filter.id))
      ?.map((filter) => filter.value)

    return selectedFilterValue
  }

  const selectedDays = getSeletedFilterValues('dayOfWeek[]') ?? []

  logEvent(
    RudderEvent.AVAILABLE_PROVIDERS_SHOWN,
    {
      num_matches_shown: providers?.length || 0,
      provider_ids_shown: providers?.map((p) => p.id),
      provider_count_provider_slots_shown: countDetails.provider_count,
      total_slots_shown: countDetails.slot_sum,
      location_filter: query?.location,
      date_filter: startSlotDate?.toISOString() || '',
      therapy_type_filter: selectedTherapyType || '',
      issues_filter: getSeletedFilterValues('areaOfConcern[]'),
      appointment_type_filter: selectedAppointmentType || '',
      payment_type_filter: getSelectedPaymentType(query),
      gender_filter: getSeletedFilterValues('gender[]'),
      age_filter: getSeletedFilterValues('clientAgeGroup[]'),
      language_filter: getSeletedFilterValues('language[]'),
      ...(query['timeOfDay[]'] && {
        time_of_day_filter: getSeletedFilterValues('timeOfDay[]'),
        timezone: timeZone,
      }),
      ...(selectedDays.length > 0 && {
        day_of_week_filter: selectedDays,
        timezone: timeZone,
      }),
      counseling_approach_filter: getSeletedFilterValues(
        'counselingApproach[]'
      ),
      provider_map_pin_filter: selectedPractice?.name || '',
      trigger_for_refresh: getTriggerForRefresh(query) || '',
      account_id: user?.id,
      practice_state: query.state,
      total_providers_available: totalProvidersAvailable,
      show_more_providers: showMoreProviders,
      find_more_partial_matches: findMorePartialMatches,
      relaxed_filters: [...relaxedFilters],

      ...(directProvidersCount !== undefined && {
        num_direct_matches_shown: directProvidersCount,
      }),
      ...(indirectProvidersCount !== undefined && {
        num_indirect_matches_shown: indirectProvidersCount,
      }),
      ...(!!directSlotsCountDetails && {
        total_direct_slots_shown: directSlotsCountDetails.slot_sum,
      }),
      ...(!!indirectSlotsCountDetails && {
        total_indirect_slots_shown: indirectSlotsCountDetails.slot_sum,
      }),
      //Supply reserve properties
      ...intakeSupplyReserveParams,
    },
    {
      [RudderstackIntegrations.GOOGLE_ADS]: true,
      [RudderstackIntegrations.FACEBOOK_PIXEL]: true,
    }
  )
}

export const getSelectedPaymentType = (query: ParsedUrlQuery): string => {
  const insuranceName = sessionStorage.getItem(
    sessionStorageKeys.INSURANCE_PROVIDER_NAME
  )
  return query?.insuranceId ? insuranceName : 'Self Pay'
}

//TODO: Add types to the variables below and null checks.
export const getTriggerForRefresh = (query: ParsedUrlQuery): string => {
  const previousRouterObj = JSON.parse(
    sessionStorage.getItem(sessionStorageKeys.PREVIOUS_ROUTER)
  )

  const { query: prevQueryObj, pathname } = previousRouterObj ?? {}

  if (pathname?.includes(pageRoutes.PROVIDER_PROFILE)) {
    return 'Returned from Profile Page'
  } else if (pathname?.includes(pageRoutes.PRE_SEARCH_QUESTIONNAIRE)) {
    return 'presearch_questionnaire'
  } else if (!prevQueryObj) {
    return 'First Load'
  }

  // retrieve the keys added into current query obj by comparing current query obj with previous query obj
  // also return keys whose values were changed
  const keysAddedIntoCurrQueryObj = _reduce(
    query,
    (result, value, key) =>
      _isEqual(value, prevQueryObj[key]) ? result : result.concat(key),
    []
  )

  if (
    !keysAddedIntoCurrQueryObj.length &&
    _size(query) !== _size(prevQueryObj)
  ) {
    return 'Clear Filters'
  } else if (keysAddedIntoCurrQueryObj.length > 0) {
    const paymentTypes = ['selfPay', 'insuranceId']
    const moreProviderFilters = [
      'gender[]',
      'clientAgeGroup[]',
      'language[]',
      'counselingApproach[]',
    ]

    const keyTextMapping = {
      'availabilityType[]': 'Appointment Type Filter',
      'counselingType[]': 'Therapy Type Filter',
      'areaOfConcern[]': 'Issues Filter',
      'timeOfDay[]': 'Time of Day Filter',
      'dayOfWeek[]': 'Day of Week Filter',
      slotStartDate: 'Date Filter',
      practiceId: 'Map Pin Filter',
    }

    if (keysAddedIntoCurrQueryObj.includes('location')) {
      return 'Location Filter'
    } else if (
      paymentTypes.some((val) => keysAddedIntoCurrQueryObj.includes(val))
    ) {
      return 'Payment Type Filter'
    } else if (
      moreProviderFilters.some((val) => keysAddedIntoCurrQueryObj.includes(val))
    ) {
      return 'More Filters Filter'
    } else {
      const key = keysAddedIntoCurrQueryObj[0]
      return key ? keyTextMapping[key] : ''
    }
  }
}

export const callRudderEventForBookingFailure = (
  error: Error,
  failureReason: string,
  responsibleParty: UserExistsResponse,
  dropdownPatients: PatientInfo[],
  selectedSlot: SelectedSlot,
  appointmentType = '',
  counselingType = '',
  skipDataKeys = []
): void => {
  const bookingFailureEventObj = {
    failure_code: error?.name || '',
    failure_reason: `${failureReason}. Error: ${error?.message}`,
    user_id: responsibleParty?.id,
    provider_id: selectedSlot?.provider?.id,
    patient_id:
      dropdownPatients?.length > 0 && dropdownPatients.map((p) => p.id),
    practice_state: selectedSlot?.provider?.practice?.state,
    slot_timestamp: selectedSlot?.slot?.start,
    ...(appointmentType && { appointment_type: appointmentType }),
    ...(counselingType && { therapy_type: counselingType }),
  }

  logEvent(
    RudderEvent.BOOKING_FAILED,
    _omit(bookingFailureEventObj, skipDataKeys)
  )
}

export const callRudderEventForBookingWarning = (
  warningReason: string,
  appointmentType: string,
  counselingType: string
): void => {
  const bookingFailureEventObj = {
    warning_reason: warningReason,
    appointment_type: appointmentType,
    therapy_type: counselingType,
  }

  logEvent(RudderEvent.BOOKING_WARNED, bookingFailureEventObj)
}

export const callRudderEventForBookingCrossStateWarningShown = (
  clientState: string,
  providerLicenseStates: string,
  clinicianId: string,
  clientType: 'new' | 'returning',
  visitType: 'video' | 'in_person'
): void => {
  logEvent(RudderEvent.BOOKING_CROSS_STATE_WARNING_SHOWN, {
    client_state: clientState,
    provider_license_states: providerLicenseStates,
    clinician_id: clinicianId,
    client_type: clientType,
    visit_type: visitType,
  })
}

export const callRudderEventForBookingCTAclicked = (
  buttonLocation: string,
  buttonClasses: string,
  buttonText: string,
  userId?: string
): void => {
  const bookingCTAclickedEventObj = {
    button_location: buttonLocation,
    button_classes: buttonClasses,
    button_text: buttonText,
    // Only logged-in surfaces pass this; anonymous callers omit the key entirely.
    ...(userId && { user_id: userId }),
  }

  logEvent(RudderEvent.BOOKING_CTA_CLICKED, bookingCTAclickedEventObj)
}

export const callRudderEventForDashboardBookNowClicked = (
  buttonLocation: string,
  buttonClasses: string,
  userId?: string
): void => {
  logEvent(RudderEvent.DASHBOARD_BOOK_NOW_CLICKED, {
    button_location: buttonLocation,
    button_classes: buttonClasses,
    ...(userId && { user_id: userId }),
  })
}

export const callRudderEventForSimilarProvidersShown = (
  providerId: string,
  practiceState: string,
  providersIdShown: string[],
  numberOfProviders: number
): void => {
  logEvent(RudderEvent.SIMILAR_PROVIDER_SHOWN, {
    Provider_id: providerId,
    Practice_state: practiceState,
    Provider_ids_shown: providersIdShown,
    Num_matches_shown: numberOfProviders,
  })
}

export const callRudderEventForViewAvailableProviders = (
  providerId: string,
  practiceState: string
): void => {
  logEvent(RudderEvent.SEE_AVAILABLE_PROVIDER_CLICKED, {
    Provider_id: providerId,
    Practice_state: practiceState,
  })
}

export const callRudderEventForBookingModalAllAvailabilityShown = (
  providerDetails: Provider,
  userSelectedFilters: { [key: string]: string | string[] | number },
  providerSlotsCount: number,
  paymentType: string,
  trackingOverrides?: { variant?: string; device?: string; surface?: string }
): void => {
  logEvent(RudderEvent.BOOKING_MODAL_ALL_AVAILABILITY_SHOWN, {
    provider_id: providerDetails.id,
    provider_name: `${providerDetails.firstName} ${providerDetails.lastName}`,
    practice_state: providerDetails.practice.state,
    filters: {
      age: userSelectedFilters?.client_age_group || 'null',
      appointment_type: userSelectedFilters?.availability_type || 'null',
      counseling_approach: userSelectedFilters?.counseling_approach || 'null',
      gender: userSelectedFilters?.gender || 'null',
      issues: userSelectedFilters?.issues || 'null',
      language: userSelectedFilters?.language || 'null',
      payment_type: paymentType,
      therapy_type: userSelectedFilters?.counseling_type || 'null',
    },
    count_provider_slots_shown: providerSlotsCount,
    ...trackingOverrides,
  })
}

type CrossStateEventContext = {
  clientState: string
  providerLicenseStates: string
  clinicianId: string
  clientType: 'new' | 'returning'
  visitType: 'video' | 'in_person'
}

// provider_license_states is a comma-separated string (e.g. "AR, CO"), the
// same shape Booking Cross State Warning Shown already sends.
export const getProviderLicenseStates = (provider: Provider): string =>
  provider?.licensedStates?.map(({ state }) => state).join(', ') ?? ''

// client_state is the address state for new clients and the license-key
// state for returning clients — client_type tells the two apart.
const getCrossStateEventProps = (context: CrossStateEventContext) => ({
  client_state: context.clientState,
  provider_license_states: context.providerLicenseStates,
  clinician_id: context.clinicianId,
  client_type: context.clientType,
  visit_type: context.visitType,
})

// The warning was already shown this session, so a later submit skipped it.
export const callRudderEventForCrossStateWarningSuppressed = (
  context: CrossStateEventContext
): void => {
  logEvent(
    RudderEvent.BOOKING_CROSS_STATE_WARNING_SUPPRESSED,
    getCrossStateEventProps(context)
  )
}

export const callRudderEventForCrossStateBlockShown = (
  context: CrossStateEventContext,
  // 'no_license_match' is still sent by the v1 hard block (not migrated yet).
  blockReason: 'not_licensed' | 'no_slot' | 'in_person' | 'no_license_match'
): void => {
  logEvent(RudderEvent.BOOKING_CROSS_STATE_BLOCK_SHOWN, {
    ...getCrossStateEventProps(context),
    block_reason: blockReason,
  })
}

// v1 only: free-text `user_selection`. v2 uses the `cta` variant below.
export const callRudderEventForCrossStateBlockInteraction = (
  context: CrossStateEventContext,
  userSelection:
    | 'State provider search link'
    | 'Return to search'
    | 'Call support'
): void => {
  logEvent(RudderEvent.BOOKING_CROSS_STATE_BLOCK_INTERACTION, {
    ...getCrossStateEventProps(context),
    user_selection: userSelection,
  })
}

export const callRudderEventForCrossStateBlockCta = (
  context: CrossStateEventContext,
  cta: 'state_link' | 'return_to_search' | 'call_support'
): void => {
  logEvent(RudderEvent.BOOKING_CROSS_STATE_BLOCK_INTERACTION, {
    ...getCrossStateEventProps(context),
    cta,
  })
}

export const callRudderEventForCrossStateProviderSwitched = (
  context: CrossStateEventContext,
  switchDetails: {
    originalState: string
    newState: string
    // EHR id on the new practice; clinician_id keeps the Thriveworks UUID.
    switchedProviderEhrId?: number
    trigger: 'address_step' | 'license_key_mismatch'
    // Omitted (not false) when either state is unknown, e.g. a brand-new patient.
    patientGeolocationMatchesFirstBookingState?: boolean
  }
): void => {
  logEvent(RudderEvent.BOOKING_CROSS_STATE_PROVIDER_SWITCHED, {
    ...getCrossStateEventProps(context),
    // Omitted (not undefined) for cached profiles that predate providerEhrId.
    ...(switchDetails.switchedProviderEhrId !== undefined && {
      switched_clinician_id: switchDetails.switchedProviderEhrId,
    }),
    original_state: switchDetails.originalState,
    new_state: switchDetails.newState,
    trigger: switchDetails.trigger,
    appointment_type: context.visitType,
    ...(switchDetails.patientGeolocationMatchesFirstBookingState !==
      undefined && {
      patient_geolocation_matches_first_booking_state:
        switchDetails.patientGeolocationMatchesFirstBookingState,
    }),
  })
}

// v1 only: free-text `user_selection`. v2 uses the `cta` variant below.
export const callRudderEventForCrossStateWarningInteraction = (
  context: CrossStateEventContext,
  userSelection: string,
  extraProps: Record<string, string> = {}
): void => {
  logEvent(RudderEvent.BOOKING_CROSS_STATE_WARNING_INTERACTION, {
    ...extraProps,
    ...getCrossStateEventProps(context),
    user_selection: userSelection,
  })
}

export const callRudderEventForCrossStateWarningCta = (
  context: CrossStateEventContext,
  cta: 'continue' | 'close' | 'state_link',
  extraProps: Record<string, string> = {}
): void => {
  logEvent(RudderEvent.BOOKING_CROSS_STATE_WARNING_INTERACTION, {
    ...extraProps,
    ...getCrossStateEventProps(context),
    cta,
  })
}
